import React, { useEffect, useState } from "react";
import TableHeader from "../../common/MagicPageHeader";
import Header from "../../common/MagicTableHeader";
import MagicTable from "../../common/MagicTable";
import DownloadToXlsxFile from "../../../helper/DownloadToXlsxFile";
import ImageComponent from "../../mui/Image";
import { useFetchGovernanceAndEthicsReportsQuery } from "../../../store/apis/reportsApis";

interface TabCategory {
  id: number;
  name: string;
  code: string;
}

interface GovernanceAndEthicsTableProps {
  selectedTab: TabCategory | null;
  magicPageHeaderBtn: {
    text: string;
    variant: string;
    startIcon: React.ReactNode;
    onClick: () => void;
    sx: Record<string, string>;
  }[];
  magicTableHeaderBtn: any[];
  params:any;
  isInvalidSearch: boolean;
  onPaginationChange: (pageNo: number) => void;
  onSearch: (searchTerm: string) => void;
}

const GovernanceAndEthicsTable: React.FC<GovernanceAndEthicsTableProps> = ({
  selectedTab,
  magicTableHeaderBtn,
  params,
  isInvalidSearch,
  onPaginationChange,
  onSearch,
}) => {
  const [downloadParams, setDownloadParams] = useState<any>(null);
  const [downloadTrigger, setDownloadTrigger] = useState(false);
  const columns = [
    {
        field: "centreName", 
        headerName: "Centre name",
        flex: 1,
        minWidth: 200,
        sortable: true,
      },
      {
        field: "region",
        headerName: "Region",
        flex: 1,
        minWidth: 150,
        sortable: true,
      },
      {
        field: "funder",
        headerName: "Funder",
        flex: 1,
        minWidth: 150,
        sortable: true,
      },
      {
        field: "state", // New column
        headerName: "State",
        flex: 1,
        minWidth: 150,
        sortable: true,
      },
      {
        field: "district", // New column
        headerName: "City",
        flex: 1,
        minWidth: 150,
        sortable: true,
      },
    {
      field: "cityManager",
      headerName: "City Manager",
      flex: 1,
      minWidth: 150,
      sortable: true,
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      minWidth: 150,
      sortable: true,
    },
    {
      field: "centreBusinessType",
      headerName: "Centre business type",
      flex: 1,
      minWidth: 200,
      sortable: true,
    },
    {
      field: "centerOccupancy",
      headerName: "Centre Occupancy as per plan and time sheet",
      flex: 1,
      minWidth: 300,
      sortable: true,
    },
    {
      field: "youthAttendance",
      headerName: "Youth Attendance",
      flex: 1,
      minWidth: 150,
      sortable: true,
    },
    {
      field: "communication",
      headerName: "Communication with FPD/Regular updated/CCTV",
      flex: 1,
      alignItems: "center",
      minWidth: 200,
      sortable: true,
      type: 'number',
    },
    {
      field: "qualityDocumentation",
      headerName: "Quality Documentation",
      flex: 1,
      minWidth: 200,
      sortable: true,
    },
    {
      field: "remarks",
      headerName: "Remarks",
      flex: 1,
      minWidth: 150,
      sortable: true,
    },
  ];

  const {
    data: fetchedReports = {
      reports: [], 
      totalReports: 0, 
    },isLoading
  } = useFetchGovernanceAndEthicsReportsQuery(params);
  const { data: downloadData,isLoading: downloadLoading } = useFetchGovernanceAndEthicsReportsQuery(downloadParams, {
    skip: !downloadParams,
  });
  const handleDownload = () => {
    const downloadPayload = {
      ...params,
      pageNo: "1",
      pageSize: fetchedReports?.totalReports || "10",
    };
    setDownloadParams(downloadPayload);
    setDownloadTrigger(true); 
  };

  useEffect(() => {
    if (downloadData && downloadTrigger) {
      const sheetData = [
        [
          "Centre Name",
          "Region",
          "Funder",
          "State",
          "CIty",
          "City Manager",
          "Status",
          "Centre Business Type",
          "Centre Occupancy",
          "Youth Attendance",
          "Communication",
          "Quality Documentation",
          "Remarks",
        ],
        ...(downloadData?.reports.map((report: any) => [
          report.centreName || "-",
          report.region || "-",
          report.funder || "-",
          report.state || "-",
          report.district || "-",
          report.cityManager || "-",
          report.status || "-",
          report.centreBusinessType || "-",
          report.centerOccupancy || "-",
          report.youthAttendance || "-",
          report.communication || "-",
          report.qualityDocumentation || "-",
          report.remarks || "-",
        ]) || []),
      ];
      DownloadToXlsxFile({
        fileNamePrifix: "Governance-And-Ethics-Report",
        sheetData,
      });
      setDownloadTrigger(false);  
    }
    }, [downloadData, downloadTrigger]);  

  const magicPageHeaderBtn = [
    {
      text: "Download",
      variant: "outlined",
      startIcon: (
        <ImageComponent src="/assets/images/login/exportIcon.svg" alt="Export Icon" />
      ),
      onClick: handleDownload,
      loading: downloadLoading,
      sx: {
        background: "white",
        border: "1px solid #D0D5DD",
        borderRadius: "8px",
        color: "black",
      },
    },
  ];
  
  const totalPages = Math.max(1, Math.ceil((fetchedReports?.totalReports || 0) /10));
  return (
    <>
      <TableHeader
        title={`${selectedTab?.name || "Governance & Ethics"} Report`}
        subTitle="Here you can view the report with all the details."
        buttons={magicPageHeaderBtn}
        chipLabel={`${fetchedReports?.totalReports || 0} entries`}
      />
      <Header
        buttons={magicTableHeaderBtn}
        onSearchfieldChange={(value) => onSearch(String(value))}
        isInvalidSearch={isInvalidSearch}
      />
      <MagicTable
        columns={columns}
        rows={fetchedReports?.reports} 
        handlePageChange={(event, pageNo) => onPaginationChange(pageNo)}
        currentPage={parseInt(params?.pageNo) || 1}
        loading={isLoading}
        count={totalPages}
        rowCellsSx={{ paddingLeft: "16px", textAlign: "start" }}
        headerSx={{
          paddingLeft: "16px",
          textAlign: "start",
          fontWeight: 600,
          color: "#242424",
          padding: "13px 16px",
        }}
        tableSx={{ minHeight: "300px", width: "100%" }}
        sx={{
          "& .MuiDataGrid-columnHeader": {
            backgroundColor: "#DADADA",
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: 550,
            fontSize: "14px",
          },
          "& .MuiDataGrid-row": {
            backgroundColor: "#FFFFFF",
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: "#FFFFFF",
          },
          "& .MuiDataGrid-cell": {
            display: "flex",
            alignItems: "center",
          },}}
      />
    </>
  );
};

export default GovernanceAndEthicsTable;